import { useEffect, useState } from "preact/hooks"
import { State } from "../state"
import { useStore } from "./useStore"

/**
 * Preact hook that imports a part of current state to the component.
 *
 * This hook is preferred over useAppState.
 * Unlike useAppState, useAppStateSelector will only trigger a component render when the selected value changes.
 *
 * @example
 * ```jsx
 * import { useAppStateSelector } from '@nosto/preact'
 *
 * export default () => {
 *     const query = useAppStateSelector(state => state.query)
 *
 *     return <div>
 *         Searching for {query.query}
 *     </div>
 * }
 * ```
 *
 * @group Hooks
 */
export function useAppStateSelector<Selected>(selector: (state: State) => Selected): Selected {
    const store = useStore()
    const [slice, setSlice] = useState(selector(store.getState()))
    store.onChange(selector, setSlice)
    useEffect(() => {
        return () => store.clearOnChange(setSlice)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    return slice
}
